import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    whatsappConsent: false,
    country: '',
    originAirport: '',
    destinationCountry: '',
    plan: 'yearly',
    language: language
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const gccCountries = [
    { value: 'OM', label: { en: 'Oman', ar: 'عمان' } },
    { value: 'AE', label: { en: 'United Arab Emirates', ar: 'الإمارات العربية المتحدة' } },
    { value: 'SA', label: { en: 'Saudi Arabia', ar: 'المملكة العربية السعودية' } },
    { value: 'KW', label: { en: 'Kuwait', ar: 'الكويت' } },
    { value: 'BH', label: { en: 'Bahrain', ar: 'البحرين' } },
    { value: 'QA', label: { en: 'Qatar', ar: 'قطر' } }
  ];

  const majorAirports = [
    { value: 'MCT', label: 'Muscat (MCT)' },
    { value: 'DXB', label: 'Dubai (DXB)' },
    { value: 'AUH', label: 'Abu Dhabi (AUH)' },
    { value: 'RUH', label: 'Riyadh (RUH)' },
    { value: 'JED', label: 'Jeddah (JED)' },
    { value: 'KWI', label: 'Kuwait City (KWI)' },
    { value: 'BAH', label: 'Bahrain (BAH)' },
    { value: 'DOH', label: 'Doha (DOH)' }
  ];

  const popularDestinations = [
    { value: 'TH', label: { en: 'Thailand', ar: 'تايلاند' } },
    { value: 'MY', label: { en: 'Malaysia', ar: 'ماليزيا' } },
    { value: 'EG', label: { en: 'Egypt', ar: 'مصر' } },
    { value: 'TR', label: { en: 'Turkey', ar: 'تركيا' } },
    { value: 'GB', label: { en: 'United Kingdom', ar: 'المملكة المتحدة' } },
    { value: 'FR', label: { en: 'France', ar: 'فرنسا' } },
    { value: 'IN', label: { en: 'India', ar: 'الهند' } },
    { value: 'PK', label: { en: 'Pakistan', ar: 'باكستان' } },
    { value: 'LK', label: { en: 'Sri Lanka', ar: 'سريلانكا' } },
    { value: 'US', label: { en: 'United States', ar: 'الولايات المتحدة' } }
  ];

  const plans = {
    yearly: { price: 35, name: { en: 'Premium Plan (Yearly)', ar: 'الخطة المميزة (سنوي)' } }
  };

  const t = {
    en: {
      pageTitle: 'Subscribe to Flight Deals',
      pageSubtitle: 'Get personalized flight alerts and exclusive deals',
      personalInfo: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      phone: 'WhatsApp Number (with country code)',
      phoneHint: 'e.g., +968 1234 5678 - We will send deals via WhatsApp',
      whatsappConsent: 'I agree to receive flight deals via WhatsApp',
      whatsappConsentRequired: 'Please agree to receive WhatsApp messages',
      country: 'Your Country',
      selectCountry: 'Select your country',
      travelPreferences: 'Travel Preferences',
      originAirport: 'Preferred Origin Airport',
      selectAirport: 'Select your nearest airport',
      destinationCountry: 'Preferred Destination',
      selectDestination: 'Select destination country',
      subscriptionPlan: 'Choose Your Plan',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      omrMonth: 'OMR/month',
      omr: 'OMR',
      save: 'Save',
      bestValue: 'Best Value',
      submitBtn: 'Subscribe & Proceed to Payment',
      processing: 'Processing...',
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidPhone: 'Please enter a valid WhatsApp number',
      errorTitle: 'Please fix the following errors:',
      successTitle: 'Success!',
      successMessage: 'Your subscription has been created. Redirecting to payment...',
      goBack: 'Go Back'
    },
    ar: {
      pageTitle: 'اشترك في عروض الطيران',
      pageSubtitle: 'احصل على تنبيهات مخصصة وعروض حصرية',
      personalInfo: 'المعلومات الشخصية',
      firstName: 'الاسم الأول',
      lastName: 'الاسم الأخير',
      email: 'البريد الإلكتروني',
      phone: 'رقم الواتساب (مع رمز الدولة)',
      phoneHint: 'مثال: 96812345678+ - سنرسل العروض عبر الواتساب',
      whatsappConsent: 'أوافق على استلام عروض الطيران عبر الواتساب',
      whatsappConsentRequired: 'يرجى الموافقة على استلام رسائل الواتساب',
      country: 'بلدك',
      selectCountry: 'اختر بلدك',
      travelPreferences: 'تفضيلات السفر',
      originAirport: 'مطار المغادرة المفضل',
      selectAirport: 'اختر أقرب مطار',
      destinationCountry: 'الوجهة المفضلة',
      selectDestination: 'اختر بلد الوجهة',
      subscriptionPlan: 'اختر خطتك',
      monthly: 'شهرية',
      quarterly: 'فصلية',
      yearly: 'سنوية',
      omrMonth: 'ريال/شهر',
      omr: 'ريال',
      save: 'وفر',
      bestValue: 'أفضل قيمة',
      submitBtn: 'اشترك وانتقل للدفع',
      processing: 'جاري المعالجة...',
      required: 'هذا الحقل مطلوب',
      invalidEmail: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
      invalidPhone: 'يرجى إدخال رقم واتساب صحيح',
      errorTitle: 'يرجى تصحيح الأخطاء التالية:',
      successTitle: 'نجح!',
      successMessage: 'تم إنشاء اشتراكك. جاري التوجيه إلى الدفع...',
      goBack: 'رجوع'
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = t[language].required;
    if (!formData.lastName.trim()) newErrors.lastName = t[language].required;
    
    if (!formData.email.trim()) {
      newErrors.email = t[language].required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t[language].invalidEmail;
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t[language].required;
    } else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = t[language].invalidPhone;
    }
    
    // WhatsApp consent is required
    if (!formData.whatsappConsent) {
      newErrors.whatsappConsent = t[language].whatsappConsentRequired;
    }
    
    if (!formData.country) newErrors.country = t[language].required;
    if (!formData.originAirport) newErrors.originAirport = t[language].required;
    if (!formData.destinationCountry) newErrors.destinationCountry = t[language].required;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage({ type: '', text: '' });
    
    if (!validateForm()) {
      setSubmitMessage({
        type: 'error',
        text: t[language].errorTitle
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the payment initiation endpoint for real payment
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          planName: plans[formData.plan].name[language],
          price: plans[formData.plan].price
        }),
      });

      const data = await response.json();

      if (data.success && data.paymentUrl) {
        setSubmitMessage({
          type: 'success',
          text: language === 'en' 
            ? 'Redirecting to secure payment page...' 
            : 'جاري التوجيه إلى صفحة الدفع الآمنة...'
        });
        
        // Redirect to MyFatoorah payment page
        setTimeout(() => {
          window.location.href = data.paymentUrl;
        }, 1500);
      } else {
        setSubmitMessage({
          type: 'error',
          text: data.message || 'An error occurred'
        });
      }
    } catch (error) {
      setSubmitMessage({
        type: 'error',
        text: 'Failed to create subscription. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 py-12 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-orange-500 hover:text-orange-600 flex items-center gap-2 mx-auto"
          >
            <span>{isRTL ? '→' : '←'}</span> {t[language].goBack}
          </button>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-transparent mb-4">
            {t[language].pageTitle}
          </h1>
          <p className="text-gray-600 text-lg">{t[language].pageSubtitle}</p>
        </div>

        {/* Error/Success Message */}
        {submitMessage.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            submitMessage.type === 'error' 
              ? 'bg-red-50 border border-red-200 text-red-700' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            <p className="font-semibold">
              {submitMessage.type === 'error' ? t[language].errorTitle : t[language].successTitle}
            </p>
            <p>{submitMessage.text}</p>
            {Object.keys(errors).length > 0 && (
              <ul className="mt-2 list-disc list-inside">
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Personal Information */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-orange-500">
              {t[language].personalInfo}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].firstName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].lastName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].email} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 flex items-center gap-2">
                  <span className="text-green-500">📱</span>
                  {t[language].phone} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+968 1234 5678"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <p className="text-gray-500 text-sm mt-1">{t[language].phoneHint}</p>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* WhatsApp Consent Checkbox */}
              <div className="md:col-span-2">
                <div className={`p-4 rounded-lg border-2 ${
                  formData.whatsappConsent 
                    ? 'bg-green-50 border-green-300' 
                    : errors.whatsappConsent 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="whatsappConsent"
                      checked={formData.whatsappConsent}
                      onChange={handleInputChange}
                      className="w-5 h-5 mt-0.5 text-green-500 rounded focus:ring-green-500 cursor-pointer"
                      disabled={isSubmitting}
                    />
                    <div>
                      <span className="text-gray-700 font-medium flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18c-1.665 0-3.225-.508-4.512-1.378l-3.488.914.914-3.488A7.953 7.953 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/>
                        </svg>
                        {t[language].whatsappConsent} <span className="text-red-500">*</span>
                      </span>
                      <p className="text-gray-500 text-sm mt-1">
                        {language === 'en' 
                          ? 'You will receive exclusive flight deals directly on WhatsApp' 
                          : 'ستتلقى عروض الطيران الحصرية مباشرة على الواتساب'}
                      </p>
                    </div>
                  </label>
                </div>
                {errors.whatsappConsent && <p className="text-red-500 text-sm mt-1">{errors.whatsappConsent}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].country} <span className="text-red-500">*</span>
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    errors.country ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">{t[language].selectCountry}</option>
                  {gccCountries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label[language]}
                    </option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>
            </div>
          </div>

          {/* Travel Preferences */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-pink-500">
              {t[language].travelPreferences}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].originAirport} <span className="text-red-500">*</span>
                </label>
                <select
                  name="originAirport"
                  value={formData.originAirport}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent ${
                    errors.originAirport ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">{t[language].selectAirport}</option>
                  {majorAirports.map(airport => (
                    <option key={airport.value} value={airport.value}>
                      {airport.label}
                    </option>
                  ))}
                </select>
                {errors.originAirport && <p className="text-red-500 text-sm mt-1">{errors.originAirport}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t[language].destinationCountry} <span className="text-red-500">*</span>
                </label>
                <select
                  name="destinationCountry"
                  value={formData.destinationCountry}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent ${
                    errors.destinationCountry ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">{t[language].selectDestination}</option>
                  {popularDestinations.map(dest => (
                    <option key={dest.value} value={dest.value}>
                      {dest.label[language]}
                    </option>
                  ))}
                </select>
                {errors.destinationCountry && <p className="text-red-500 text-sm mt-1">{errors.destinationCountry}</p>}
              </div>
            </div>
          </div>

          {/* Subscription Plan */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-purple-500">
              {t[language].subscriptionPlan}
            </h2>
            <div className="max-w-md mx-auto">
              {Object.entries(plans).map(([key, plan]) => (
                <div
                  key={key}
                  className="relative p-8 border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-2xl"
                >
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-4 py-2 rounded-full">
                    {t[language].bestValue}
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-2xl mb-3 text-purple-800">{plan.name[language]}</h3>
                    <div className="text-5xl font-bold text-purple-600 mb-3">
                      {plan.price} {t[language].omr}
                    </div>
                    <div className="text-lg text-gray-700 mb-2">
                      {(plan.price / 12).toFixed(2)} {t[language].omrMonth}
                    </div>
                    <div className="inline-block bg-green-100 text-green-700 font-semibold px-4 py-2 rounded-full mt-2">
                      💰 {t[language].save} 70%
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                      {language === 'en' ? 'Compared to monthly pricing' : 'مقارنة بالتسعير الشهري'}
                    </p>
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">✓</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white font-bold py-4 px-8 rounded-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t[language].processing : t[language].submitBtn}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionPage;